import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Lightweight polling notifications bell. Injected into Navbar for
 * authenticated users. Polls /api/notifications every 30s (only when the
 * tab is visible) so we don't hammer the API in background tabs.
 */
export default function NotificationsBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) { /* silent — auth may have expired */ }
  };

  useEffect(() => {
    load();
    let iv;
    const start = () => { iv = setInterval(() => { if (!document.hidden) load(); }, 30000); };
    const stop = () => { if (iv) clearInterval(iv); };
    start();
    const vis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", vis);
    return () => { stop(); document.removeEventListener("visibilitychange", vis); };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read", { all: true });
      await load();
    } catch (e) { toast.error("Failed to mark read"); }
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) await api.post("/notifications/read", { ids: [n.id] });
    } catch (_) {}
    if (n.link) nav(n.link);
    setOpen(false);
    load();
  };

  return (
    <div className="relative" ref={boxRef} data-testid="notif-bell-wrap">
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="Notifications"
        data-testid="notif-bell"
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-slate-100 hover:text-teal-300 hover:bg-white/5 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-slate-950"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-h-[480px] overflow-hidden rounded-2xl bg-slate-900 border border-white/10 shadow-2xl z-50"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm font-semibold text-slate-100">Notifications</div>
            <button
              onClick={markAllRead}
              disabled={unread === 0}
              data-testid="notif-mark-all"
              className="text-xs text-teal-300 hover:text-teal-200 disabled:text-slate-500 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Check className="w-3 h-3" />Mark all read
            </button>
          </div>
          <div className="overflow-y-auto max-h-[400px]" data-testid="notif-list">
            {items.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">You're all caught up.</div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${n.read ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-teal-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] font-mono text-slate-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
